import { Helmet } from 'react-helmet-async';

const MAIN_DOMAIN = "https://www.fixswift.in";

interface SEOManagerProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  ogImage?: string;
  url?: string;
  canonical?: string;
  type?: 'website' | 'article' | 'product';
  schemaOrg?: Record<string, any> | any[];
  jsonLd?: Record<string, any> | any[];
  noindex?: boolean;
}

const SEOManager = ({
  title = "Fixswift Computers - Premium Refurbished Laptops",
  description = "India's most trusted brand for premium refurbished Apple MacBooks and Windows laptops. 21-point check, certified quality, and easy returns.",
  keywords = "refurbished laptops, second hand laptops, buy apple macbook, used laptops india",
  image,
  ogImage,
  url,
  canonical,
  type = 'website',
  schemaOrg,
  jsonLd,
  noindex = false,
}: SEOManagerProps) => {
  // Always resolve canonical to main domain
  const path = typeof window !== 'undefined' ? window.location.pathname : '/';
  const resolvedCanonical = canonical || url || `${MAIN_DOMAIN}${path}`;
  // Ensure canonical always points to main domain
  const finalUrl = resolvedCanonical.includes("lovable.app") 
    ? resolvedCanonical.replace(/https?:\/\/[^/]*lovable\.app/, MAIN_DOMAIN)
    : resolvedCanonical;
  
  const finalImage = ogImage || image || `${MAIN_DOMAIN}/og-image.png`;
  const finalSchema = jsonLd || schemaOrg;

  // Auto-noindex on preview domains
  const isPreview = typeof window !== 'undefined' && window.location.hostname.includes("lovable.app");
  const shouldNoindex = noindex || isPreview;

  const formattedTitle = title === "Fixswift Computers - Premium Refurbished Laptops" 
    ? title 
    : title.includes("Fixswift") || title.includes("FixSwift") 
      ? title 
      : `${title} | FixSwift Computers`;

  // BreadcrumbList schema from URL path
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": MAIN_DOMAIN },
      ...(path !== '/' ? [{
        "@type": "ListItem",
        "position": 2,
        "name": formattedTitle.split('|')[0].trim(),
        "item": `${MAIN_DOMAIN}${path}`
      }] : [])
    ]
  };

  return (
    <Helmet>
      <title>{formattedTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      
      {shouldNoindex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      )}

      <link rel="canonical" href={finalUrl} />

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={finalUrl} />
      <meta property="og:title" content={formattedTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:site_name" content="FixSwift Computers" />
      {finalImage && <meta property="og:image" content={finalImage} />}
      <meta property="og:locale" content="en_IN" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={finalUrl} />
      <meta name="twitter:title" content={formattedTitle} />
      <meta name="twitter:description" content={description} />
      {finalImage && <meta name="twitter:image" content={finalImage} />}

      {/* Breadcrumb Schema - auto-generated */}
      <script type="application/ld+json">
        {JSON.stringify(breadcrumbSchema)}
      </script>

      {/* Page-specific Schema */}
      {finalSchema && (
        <script type="application/ld+json">
          {JSON.stringify(finalSchema)}
        </script>
      )}
    </Helmet>
  );
};

export default SEOManager;
